/**
 * Signed v2 API client for the web account portal. HMAC-signs every request
 * (Web Crypto) exactly like vpnee-cap. The APP_SECRET is extractable from the
 * bundle — it only gates casual scraping; the real per-user gate is the bearer
 * token issued at login. Backend must carry a 'vpnee-web' app_clients entry.
 */
export const ENDPOINT_V2 = 'https://vnav.lk/api/vpnee/v2';
const APP_ID = 'vpnee-web';
const APP_SECRET = 'e9c8e363ea09cab1ccff9463093ea52ebe9898fd11bb15f476d31add35920d9f';

export type ApiResult<T = any> = { success: boolean; message?: string; data?: T; status?: number };

export const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch {} },
  del: (k: string) => { try { localStorage.removeItem(k); } catch {} },
};

const enc = new TextEncoder();
const toHex = (b: ArrayBuffer) => [...new Uint8Array(b)].map(x => x.toString(16).padStart(2, '0')).join('');
const sha256 = async (m: string) => toHex(await crypto.subtle.digest('SHA-256', enc.encode(m)));
async function hmac(secret: string, msg: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return toHex(await crypto.subtle.sign('HMAC', key, enc.encode(msg)));
}
function nonce(): string {
  const a = new Uint8Array(16); crypto.getRandomValues(a);
  return [...a].map(b => b.toString(16).padStart(2, '0')).join('');
}
function qs(q?: Record<string, string>): string {
  return q ? Object.keys(q).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(q[k])}`).join('&') : '';
}

async function signed(method: 'GET' | 'POST', subPath: string, opts: { query?: Record<string, string>; body?: object } = {}): Promise<{ status: number; data: any }> {
  const query = qs(opts.query);
  const bodyStr = opts.body !== undefined ? JSON.stringify(opts.body) : '';
  const ts = Math.floor(Date.now() / 1000).toString();
  const non = nonce();
  const canonical = [method, subPath, query, ts, non, await sha256(bodyStr)].join('\n');
  const sig = await hmac(APP_SECRET, canonical);
  const headers: Record<string, string> = { 'X-App-Id': APP_ID, 'X-Timestamp': ts, 'X-Nonce': non, 'X-Signature': sig };
  if (bodyStr) headers['Content-Type'] = 'application/json';
  const token = store.get('vpnee_token');
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const url = ENDPOINT_V2 + subPath + (query ? '?' + query : '');
  const r = await fetch(url, { method, headers, body: bodyStr || undefined });
  let data: any = null; try { data = JSON.parse(await r.text()); } catch {}
  if (r.status === 401 && token) { store.del('vpnee_token'); store.del('vpnee_user'); }
  return { status: r.status, data };
}

function deviceId(): string {
  let id = store.get('device_id');
  if (!id) { id = 'web-' + Math.random().toString(36).slice(2) + Date.now().toString(36); store.set('device_id', id); }
  return id;
}
const deviceMeta = () => ({ device_brand: 'web', device_model: navigator.platform || 'Browser', os_version: navigator.userAgent.slice(0, 80), app_version: '1.0.0', platform: 'web' });

export const API = {
  token: () => store.get('vpnee_token'),
  loggedIn: () => !!store.get('vpnee_token'),
  logout() { if (store.get('vpnee_token')) signed('POST', '/logout').catch(() => {}); store.del('vpnee_token'); store.del('vpnee_user'); },

  async login(u: string, p: string): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/login', { body: { username: u, password: p, device_id: deviceId(), ...deviceMeta() } });
      if (status === 200 && data?.success) { store.set('vpnee_token', data.token); store.set('vpnee_user', JSON.stringify(data.user)); return { success: true, data: data.user }; }
      return { success: false, message: data?.message || 'Invalid credentials' };
    } catch { return { success: false, message: 'Network error' }; }
  },
  // STEP 1 — emails a 6-digit OTP; account is not created until verifyOtp().
  async register(u: string, e: string, p: string): Promise<ApiResult> {
    try {
      const { data } = await signed('POST', '/register', { body: { username: u, email: e, password: p, device_id: deviceId(), ...deviceMeta() } });
      if (data?.success && data?.otp_required) return { success: true, data: { otp_required: true, email: data.email } };
      // Legacy backend (no OTP) may still return a token directly.
      if (data?.success && data?.token) { store.set('vpnee_token', data.token); if (data.user) store.set('vpnee_user', JSON.stringify(data.user)); return { success: true, data: { otp_required: false } }; }
      return { success: false, message: data?.message || 'Registration failed' };
    } catch { return { success: false, message: 'Network error' }; }
  },
  // STEP 2 — confirm the emailed code; on success we are logged in.
  async verifyOtp(email: string, code: string): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/register/verify', { body: { email, code, device_id: deviceId(), ...deviceMeta() } });
      if (status === 200 && data?.success && data?.token) { store.set('vpnee_token', data.token); store.set('vpnee_user', JSON.stringify(data.user)); return { success: true }; }
      return { success: false, message: data?.message || 'Verification failed' };
    } catch { return { success: false, message: 'Network error' }; }
  },
  async resendOtp(email: string): Promise<ApiResult> {
    try {
      const { data } = await signed('POST', '/register/resend', { body: { email } });
      return { success: !!data?.success, message: data?.message };
    } catch { return { success: false, message: 'Network error' }; }
  },
  async stats(): Promise<ApiResult> { try { const { data } = await signed('GET', '/user/stats'); return data?.success ? { success: true, data } : { success: false }; } catch { return { success: false }; } },
  async invoices(): Promise<ApiResult> { try { const { data } = await signed('GET', '/user/invoices'); return { success: true, data: data?.invoices || [] }; } catch { return { success: false }; } },
  async devices(): Promise<ApiResult> { try { const { data } = await signed('GET', '/user/devices'); return { success: true, data: data?.devices || [] }; } catch { return { success: false }; } },
  async traffic(): Promise<ApiResult> { try { const { data } = await signed('GET', '/user/traffic'); return { success: true, data: data?.logs || [] }; } catch { return { success: false }; } },
  // Validate/redeem a promo code. free_days → applied instantly (reload stats);
  // discount → returns discount_percentage to carry into paymentIntent.
  async applyCoupon(code: string): Promise<ApiResult> { try { const { status, data } = await signed('POST', '/coupon/apply', { body: { code } }); return status === 200 && data?.success !== false ? { success: true, data } : { success: false, message: data?.message || 'Invalid promo code' }; } catch { return { success: false, message: 'Network error' }; } },
  async repayIntent(orderId: string, method='card', phone=''): Promise<{ url: string; token?: string; order_id?: string; amount?: number; currency?: string; client_secret?: string; publishable_key?: string; error?: string } | null> {
    try {
      const { status, data } = await signed('POST', '/payment/repay-intent', { body: { order_id: orderId, method, phone } });
      if (!data) return null;
      if (data.success === false || status >= 400) return { url: '', error: data.message || `Error ${status}` } as any;
      if (data.url || data.client_secret) return data;
      if (typeof data === 'string') return { url: data, order_id: orderId };
      return { url: '', error: data.message || 'Gateway error' } as any;
    } catch (e:any) { return { url: '', error: e?.message || 'Network error' } as any; }
  },
  async revokeDevice(id: string): Promise<ApiResult> { try { const { data } = await signed('POST', '/user/devices/revoke', { body: { device_id: id } }); return data || { success: false }; } catch { return { success: false }; } },
  async deleteAccount(): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/user/delete-account');
      if (status === 200 && data?.success) {
        store.del('vpnee_token'); store.del('vpnee_user');
        return { success: true, message: data?.message };
      }
      return { success: false, message: data?.message || 'Delete failed' };
    } catch { return { success: false, message: 'Network error' }; }
  },
  async supportSend(message: string): Promise<ApiResult> { try { const { data } = await signed('POST', '/user/support/send', { body: { message } }); return data || { success: false }; } catch { return { success: false }; } },
  async supportMessages(after = 0): Promise<ApiResult> { try { const { data } = await signed('GET', '/user/support/messages', { query: { after: String(after) } }); return data || { success: false }; } catch { return { success: false }; } },
  // Legacy — was the backend PayHere hosted-form URL. Now returns the SPA
  // /payments page so anything still calling this lands on Stripe direct-card.
  checkoutUrl(plan: string) { return `/payments?plan=${encodeURIComponent(plan)}`; },

  /** Consume the handoff minted by /user/web-handoff on the native side.
   *  Returns { token, user } which /account/sso writes to localStorage. */
  async exchangeSso(handoff: string): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/session/sso-exchange', { body: { handoff } });
      if (status === 200 && data?.success !== false && data?.token) return { success: true, data };
      return { success: false, message: data?.message || 'Handoff expired' };
    } catch { return { success: false, message: 'Network error' }; }
  },

  // ── Google/Facebook OAuth (browser-facing, ?client=web routes callback here) ─
  // OAuth *launch* rides the v1 URL — v1 callback URIs are the ones registered
  // in the Google/FB consoles. Backend echoes state=web back → success redirects
  // to /account/callback same-origin. Confirmed 2026-08-17.
  socialLoginUrl(provider: 'google' | 'facebook', mode: 'login' | 'register' = 'login') {
    return `https://vnav.lk/api/vpnee/social/${provider}?client=web&device_id=${encodeURIComponent(deviceId())}&mode=${mode}`;
  },
  async socialLogin(email: string, provider: string, handoff: string): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/social', { body: { email, provider, handoff, device_id: deviceId(), ...deviceMeta() } });
      if (status === 200 && data?.success) { store.set('vpnee_token', data.token); store.set('vpnee_user', JSON.stringify(data.user)); return { success: true, data: data.user }; }
      return { success: false, message: data?.message || 'Social login failed' };
    } catch { return { success: false, message: 'Network error' }; }
  },
  // Ask backend for a signed one-shot payment URL. `method` selects the gateway
  // (card | onepay | alipay); backend routes to the right processor. Returns
  // {url, token, order_id, amount, currency} — /payments checkout page uses
  // token as the receipt reference and url as the "Pay now" redirect.
  async paymentIntent(plan: string, coupon = '', method = 'payhere', phone = ''): Promise<{
    url: string; token?: string; order_id?: string; amount?: number; currency?: string;
    client_secret?: string; publishable_key?: string;
    error?: string;
  } | null> {
    try {
      const { status, data } = await signed('POST', '/payment/intent', { body: { plan, coupon, method, phone } });
      if (!data) return null;
      if (data.success === false || status >= 400) return { url: '', error: data.message || `Error ${status}` } as any;
      if (!data.url && !data.client_secret && !data.payhere) return { url: '', error: data.message || 'Gateway error' } as any;
      return {
        url: data.url || '',
        token: data.token, order_id: data.order_id, amount: data.amount ?? data.amount_num, currency: data.currency,
        client_secret: data.client_secret, publishable_key: data.publishable_key,
        // PayHere passthrough
        payhere: data.payhere, payhere_url: data.payhere_url, merchant_id: data.merchant_id, hash: data.hash,
        items: data.items, first_name: data.first_name, last_name: data.last_name, email: data.email, phone: data.phone,
        address: data.address, city: data.city, country: data.country, return_url: data.return_url, cancel_url: data.cancel_url, notify_url: data.notify_url,
        amount_num: data.amount_num,
        ...data,
      } as any;
    } catch (e:any) { return { url: '', error: e?.message || 'Network error' } as any; }
  },
  // Live site-wide promo, if any. `null` if none active. Clients show a banner
  // + auto-apply at checkout (server also enforces, so it's real either way).
  async activePromo(): Promise<any | null> {
    try {
      const { data } = await signed('GET', '/user/active-promo');
      return (data && data.promo) || null;
    } catch { return null; }
  },
  async cancelPlan(reason = ''): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/user/cancel-plan', { body: { reason } });
      if (status === 200 && data?.success !== false) return { success: true, data, status };
      return { success: false, message: data?.message || 'Cancel failed', data, status, ...(data || {}) } as any;
    } catch { return { success: false, message: 'Network error' }; }
  },
  async appeal(message: string): Promise<ApiResult> {
    try {
      const { status, data } = await signed('POST', '/user/appeal', { body: { message } });
      if (status === 200 && data?.success !== false) return { success: true, data };
      return { success: false, message: data?.message || 'Appeal failed' };
    } catch { return { success: false, message: 'Network error' }; }
  },

  // ── Storage — presigned URLs (keys never leave the server) ───────────────
  // The browser never holds S3 keys. These helpers ask the backend (HMAC +
  // bearer required) for a short-lived PUT/GET URL, then the browser talks
  // directly to storage. Validates bucket/folder/mime/size server-side.
  async presignUpload(opts: { filename: string; contentType?: string; folder?: string; bucket?: string; size?: number }): Promise<{ key: string; bucket: string; uploadUrl: string; publicUrl: string; expiresIn: number } | null> {
    try {
      const { status, data } = await signed('POST', '/storage/presign-upload', { body: opts });
      if (status === 200 && data?.success && data.uploadUrl) return data as any;
      return null;
    } catch { return null; }
  },
  async presignDownload(key: string, bucket?: string, expiresIn?: number): Promise<string | null> {
    try {
      const { status, data } = await signed('POST', '/storage/presign-download', { body: { key, bucket, expiresIn } });
      if (status === 200 && data?.success && data.url) return data.url as string;
      return null;
    } catch { return null; }
  },
  /** Convenience: presign then PUT a File/Blob directly to storage. Returns public URL. */
  async uploadFile(file: File, folder = 'uploads'): Promise<string | null> {
    const presigned = await API.presignUpload({ filename: file.name, contentType: file.type || 'application/octet-stream', folder, size: file.size });
    if (!presigned) return null;
    const r = await fetch(presigned.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file });
    if (!r.ok) return null;
    return presigned.publicUrl;
  },
};
